// client/src/components/fornecedores/FornecedorForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useFornecedorForm } from '../../hooks/useFornecedores';
import { XCircle, Plus, Trash2, Phone, User, CheckCircle2, Loader2 } from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import { lookupCep } from '../../utils/cepService';
import { FormStepper, FormStepNavigation } from '../shared/FormStepper';

const STEP_LABELS = ['Dados Básicos', 'Endereço', 'Contatos', 'Dados Financeiros', 'Informações Adicionais'];

const initialState = {
  // Dados básicos
  tipoPessoa: 'Jurídica',
  razaoSocial: '',
  nomeFantasia: '',
  documentoPrincipal: '',
  emailPrincipal: '',
  telefonePrincipal: '',
  observacoes: '',
  ativo: true,

  // Endereço
  endereco: {
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    pais: 'Brasil'
  },

  // Contatos
  contatos: [],

  // Telefones adicionais
  telefones: [],

  // Dados bancários
  dadosBancarios: {
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: 'corrente',
    pix: ''
  },

  // Categorias
  categoriasFornecidas: [],

  // Metadados
  metadata: {
    tempoResposta: '',
    avaliacao: '',
    dataInicioParceria: '',
    website: '',
    redesSociais: {
      linkedin: '',
      instagram: '',
      facebook: ''
    }
  }
};

const contatoInicial = {
  nome: '',
  cargo: '',
  email: '',
  telefone: '',
  principal: false
};

const telefoneInicial = {
  tipo: 'comercial',
  numero: '',
  observacao: ''
};

const FornecedorForm = ({ fornecedor, onCloseForm, onSuccess }) => {
  const { salvarFornecedor, isLoading } = useFornecedorForm(fornecedor?._id);
  const { setAlert } = useAlert();
  const [formData, setFormData] = useState(initialState);
  const [currentStep, setCurrentStep] = useState(1);
  const [novoContato, setNovoContato] = useState(contatoInicial);
  const [novoTelefone, setNovoTelefone] = useState(telefoneInicial);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [cepEncontrado, setCepEncontrado] = useState(false);

  useEffect(() => {
    if (fornecedor) {
      setFormData({
        ...initialState,
        ...fornecedor,
        endereco: { ...initialState.endereco, ...fornecedor.endereco },
        dadosBancarios: { ...initialState.dadosBancarios, ...fornecedor.dadosBancarios },
        metadata: { 
          ...initialState.metadata, 
          ...fornecedor.metadata,
          redesSociais: { ...initialState.metadata.redesSociais, ...fornecedor.metadata?.redesSociais }
        }
      });
    } else {
      setFormData(initialState);
    }
  }, [fornecedor]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEnderecoChange = (e) => {
    const { name, value } = e.target;
    if (name === 'cep') setCepEncontrado(false);
    setFormData(prev => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [name]: value
      }
    }));
  };

  const handleCepBlur = useCallback(async (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length !== 8) return;
    setIsCepLoading(true);
    try {
      const addr = await lookupCep(raw);
      setFormData(prev => ({
        ...prev,
        endereco: {
          ...prev.endereco,
          cep:       addr.cep       || prev.endereco.cep,
          logradouro: addr.logradouro || prev.endereco.logradouro,
          bairro:    addr.bairro    || prev.endereco.bairro,
          cidade:    addr.localidade || prev.endereco.cidade,
          estado:    addr.uf        || prev.endereco.estado,
        }
      }));
      setCepEncontrado(true);
      setAlert('CEP encontrado com sucesso!', 'success');
    } catch (err) {
      setAlert(err.message || 'Não foi possível consultar o CEP.', 'danger');
    } finally {
      setIsCepLoading(false);
    }
  }, [setAlert]);

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        [name]: value
      }
    }));
  };

  const handleRedesSociaisChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        redesSociais: {
          ...prev.metadata.redesSociais,
          [name]: value
        }
      }
    }));
  };

  const handleDadosBancariosChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      dadosBancarios: {
        ...prev.dadosBancarios,
        [name]: value
      }
    }));
  };

  // Contatos
  const handleContatoChange = (index, field, value) => {
    const updatedContatos = [...formData.contatos];
    updatedContatos[index][field] = value;
    setFormData(prev => ({ ...prev, contatos: updatedContatos }));
  };

  const adicionarContato = () => {
    if (novoContato.nome && novoContato.email) {
      setFormData(prev => ({
        ...prev,
        contatos: [...prev.contatos, { ...novoContato }]
      }));
      setNovoContato(contatoInicial);
    }
  };

  const removerContato = (index) => {
    const updatedContatos = formData.contatos.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, contatos: updatedContatos }));
  };

  // Telefones
  const handleTelefoneChange = (index, field, value) => {
    const updatedTelefones = [...formData.telefones];
    updatedTelefones[index][field] = value;
    setFormData(prev => ({ ...prev, telefones: updatedTelefones }));
  };

  const adicionarTelefone = () => {
    if (novoTelefone.numero) {
      setFormData(prev => ({
        ...prev,
        telefones: [...prev.telefones, { ...novoTelefone }]
      }));
      setNovoTelefone(telefoneInicial);
    }
  };

  const removerTelefone = (index) => {
    const updatedTelefones = formData.telefones.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, telefones: updatedTelefones }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salvarFornecedor(formData);
      onSuccess();
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
    }
  };

  const formatarDocumento = (documento, tipoPessoa) => {
    if (!documento) return '';
    const doc = documento.replace(/\D/g, '');
    if (tipoPessoa === 'Física') {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-header">
        <h3 className="form-title">
          {fornecedor ? 'Editar Fornecedor' : 'Adicionar Novo Fornecedor'}
        </h3>
        <button type="button" title="Fechar Formulário" className="form-close-button" onClick={onCloseForm}>
          <XCircle size={24} aria-hidden="true" />
        </button>
      </div>

      <FormStepper currentStep={currentStep} steps={STEP_LABELS} />

      <div className="form-tabs-content">
        {/* ABA: Dados Básicos */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="tipoPessoa" className="form-label">Tipo de Pessoa *</label>
                <select
                  name="tipoPessoa"
                  value={formData.tipoPessoa}
                  onChange={handleChange}
                  className="form-group-select"
                  required
                >
                  <option value="Jurídica">Pessoa Jurídica</option>
                  <option value="Física">Pessoa Física</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="documentoPrincipal" className="form-label">
                  {formData.tipoPessoa === 'Física' ? 'CPF *' : 'CNPJ *'}
                </label>
                <input
                  type="text"
                  name="documentoPrincipal"
                  value={formatarDocumento(formData.documentoPrincipal, formData.tipoPessoa)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setFormData(prev => ({ ...prev, documentoPrincipal: value }));
                  }}
                  className="form-group-input"
                  required
                  maxLength={formData.tipoPessoa === 'Física' ? 14 : 18}
                />
              </div>

              <div className="form-group md:col-span-2">
                <label htmlFor="razaoSocial" className="form-label">
                  {formData.tipoPessoa === 'Física' ? 'Nome Completo *' : 'Razão Social *'}
                </label>
                <input
                  type="text"
                  name="razaoSocial"
                  value={formData.razaoSocial}
                  onChange={handleChange}
                  className="form-group-input"
                  required
                />
              </div>

              <div className="form-group md:col-span-2">
                <label htmlFor="nomeFantasia" className="form-label">
                  {formData.tipoPessoa === 'Física' ? 'Apelido/Nome Social' : 'Nome Fantasia'}
                </label>
                <input
                  type="text"
                  name="nomeFantasia"
                  value={formData.nomeFantasia}
                  onChange={handleChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="emailPrincipal" className="form-label">Email Principal *</label>
                <input
                  type="email"
                  name="emailPrincipal"
                  value={formData.emailPrincipal}
                  onChange={handleChange}
                  className="form-group-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefonePrincipal" className="form-label">Telefone Principal *</label>
                <input
                  type="text"
                  name="telefonePrincipal"
                  value={formData.telefonePrincipal}
                  onChange={handleChange}
                  className="form-group-input"
                  required
                />
              </div>

              <div className="form-group md:col-span-2">
                <label htmlFor="observacoes" className="form-label">Observações</label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows="3"
                  className="form-group-input"
                  placeholder="Informações adicionais sobre o fornecedor..."
                />
              </div>
            </div>
          </div>
        )}

        {/* ABA: Endereço */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group md:col-span-2">
                <label htmlFor="logradouro" className="form-label">Logradouro</label>
                <input
                  type="text"
                  name="logradouro"
                  value={formData.endereco.logradouro}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                  placeholder="Rua, Avenida, etc."
                  readOnly={cepEncontrado}
                  style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="numero" className="form-label">Número</label>
                <input
                  type="text"
                  name="numero"
                  value={formData.endereco.numero}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="complemento" className="form-label">Complemento</label>
                <input
                  type="text"
                  name="complemento"
                  value={formData.endereco.complemento}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bairro" className="form-label">Bairro</label>
                <input
                  type="text"
                  name="bairro"
                  value={formData.endereco.bairro}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                  readOnly={cepEncontrado}
                  style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="cidade" className="form-label">Cidade</label>
                <input
                  type="text"
                  name="cidade"
                  value={formData.endereco.cidade}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                  readOnly={cepEncontrado}
                  style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="estado" className="form-label">Estado</label>
                <input
                  type="text"
                  name="estado"
                  value={formData.endereco.estado}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                  maxLength="2"
                  placeholder="UF"
                  readOnly={cepEncontrado}
                  style={cepEncontrado ? { backgroundColor: 'var(--color-bg-subtle, #f1f5f9)', cursor: 'not-allowed' } : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="cep" className="form-label">CEP</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    type="text"
                    name="cep"
                    value={formData.endereco.cep}
                    onChange={handleEnderecoChange}
                    onBlur={handleCepBlur}
                    disabled={isCepLoading}
                    className="form-group-input"
                    placeholder="00000-000"
                    maxLength={9}
                    style={{ paddingRight: isCepLoading ? '2.25rem' : undefined }}
                  />
                  {isCepLoading && (
                    <Loader2 size={16} className="animate-spin"
                      style={{ position: 'absolute', right: '0.75rem', color: '#64748b', pointerEvents: 'none' }} />
                  )}
                </div>
                {cepEncontrado ? (
                  <div className="form-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <CheckCircle2 size={13} style={{ color: 'var(--color-success, #16a34a)', flexShrink: 0 }} />
                    Endereço preenchido automaticamente.
                    <button type="button" onClick={() => setCepEncontrado(false)}
                      style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                      Editar manualmente
                    </button>
                  </div>
                ) : (
                  <div className="form-hint">Digite o CEP e aguarde o preenchimento automático</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="pais" className="form-label">País</label>
                <input
                  type="text"
                  name="pais"
                  value={formData.endereco.pais}
                  onChange={handleEnderecoChange}
                  className="form-group-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* ABA: Contatos */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Telefones Adicionais */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-4 flex items-center">
                <Phone size={16} className="mr-2" />
                Telefones Adicionais
              </h4>
              
              {formData.telefones.map((telefone, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4 p-3 bg-gray-50 rounded">
                  <select
                    value={telefone.tipo}
                    onChange={(e) => handleTelefoneChange(index, 'tipo', e.target.value)}
                    className="form-group-select"
                  >
                    <option value="comercial">Comercial</option>
                    <option value="celular">Celular</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="fax">Fax</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Número"
                    value={telefone.numero}
                    onChange={(e) => handleTelefoneChange(index, 'numero', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <input
                    type="text"
                    placeholder="Observação"
                    value={telefone.observacao}
                    onChange={(e) => handleTelefoneChange(index, 'observacao', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <button
                    type="button"
                    onClick={() => removerTelefone(index)}
                    className="action-icon-button action-icon-delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <select
                  value={novoTelefone.tipo}
                  onChange={(e) => setNovoTelefone(prev => ({ ...prev, tipo: e.target.value }))}
                  className="form-group-select"
                >
                  <option value="comercial">Comercial</option>
                  <option value="celular">Celular</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="fax">Fax</option>
                </select>
                
                <input
                  type="text"
                  placeholder="Número"
                  value={novoTelefone.numero}
                  onChange={(e) => setNovoTelefone(prev => ({ ...prev, numero: e.target.value }))}
                  className="form-group-input"
                />
                
                <input
                  type="text"
                  placeholder="Observação"
                  value={novoTelefone.observacao}
                  onChange={(e) => setNovoTelefone(prev => ({ ...prev, observacao: e.target.value }))}
                  className="form-group-input"
                />
                
                <button
                  type="button"
                  onClick={adicionarTelefone}
                  className="action-button action-button-secondary"
                >
                  <Plus size={16} className="mr-1" />
                  Adicionar
                </button>
              </div>
            </div>

            {/* Contatos */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-4 flex items-center">
                <User size={16} className="mr-2" />
                Pessoas de Contato
              </h4>
              
              {formData.contatos.map((contato, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 p-3 bg-gray-50 rounded">
                  <input
                    type="text"
                    placeholder="Nome"
                    value={contato.nome}
                    onChange={(e) => handleContatoChange(index, 'nome', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <input
                    type="text"
                    placeholder="Cargo"
                    value={contato.cargo}
                    onChange={(e) => handleContatoChange(index, 'cargo', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <input
                    type="email"
                    placeholder="Email"
                    value={contato.email}
                    onChange={(e) => handleContatoChange(index, 'email', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <input
                    type="text"
                    placeholder="Telefone"
                    value={contato.telefone}
                    onChange={(e) => handleContatoChange(index, 'telefone', e.target.value)}
                    className="form-group-input"
                  />
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={contato.principal}
                      onChange={(e) => handleContatoChange(index, 'principal', e.target.checked)}
                      className="form-checkbox"
                    />
                    <label className="text-sm text-gray-600">Principal</label>
                    <button
                      type="button"
                      onClick={() => removerContato(index)}
                      className="action-icon-button action-icon-delete ml-2"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                <input
                  type="text"
                  placeholder="Nome"
                  value={novoContato.nome}
                  onChange={(e) => setNovoContato(prev => ({ ...prev, nome: e.target.value }))}
                  className="form-group-input"
                />
                
                <input
                  type="text"
                  placeholder="Cargo"
                  value={novoContato.cargo}
                  onChange={(e) => setNovoContato(prev => ({ ...prev, cargo: e.target.value }))}
                  className="form-group-input"
                />
                
                <input
                  type="email"
                  placeholder="Email"
                  value={novoContato.email}
                  onChange={(e) => setNovoContato(prev => ({ ...prev, email: e.target.value }))}
                  className="form-group-input"
                />
                
                <input
                  type="text"
                  placeholder="Telefone"
                  value={novoContato.telefone}
                  onChange={(e) => setNovoContato(prev => ({ ...prev, telefone: e.target.value }))}
                  className="form-group-input"
                />
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={novoContato.principal}
                    onChange={(e) => setNovoContato(prev => ({ ...prev, principal: e.target.checked }))}
                    className="form-checkbox"
                  />
                  <label className="text-sm text-gray-600">Principal</label>
                  <button
                    type="button"
                    onClick={adicionarContato}
                    className="action-button action-button-secondary ml-2"
                  >
                    <Plus size={16} className="mr-1" />
                    Adicionar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ABA: Dados Financeiros */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="banco" className="form-label">Banco</label>
                <input
                  type="text"
                  name="banco"
                  value={formData.dadosBancarios.banco}
                  onChange={handleDadosBancariosChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="agencia" className="form-label">Agência</label>
                <input
                  type="text"
                  name="agencia"
                  value={formData.dadosBancarios.agencia}
                  onChange={handleDadosBancariosChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="conta" className="form-label">Conta</label>
                <input
                  type="text"
                  name="conta"
                  value={formData.dadosBancarios.conta}
                  onChange={handleDadosBancariosChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tipoConta" className="form-label">Tipo de Conta</label>
                <select
                  name="tipoConta"
                  value={formData.dadosBancarios.tipoConta}
                  onChange={handleDadosBancariosChange}
                  className="form-group-select"
                >
                  <option value="corrente">Corrente</option>
                  <option value="poupanca">Poupança</option>
                </select>
              </div>

              <div className="form-group md:col-span-2">
                <label htmlFor="pix" className="form-label">Chave PIX</label>
                <input
                  type="text"
                  name="pix"
                  value={formData.dadosBancarios.pix}
                  onChange={handleDadosBancariosChange}
                  className="form-group-input"
                  placeholder="CPF/CNPJ, email, telefone ou chave aleatória"
                />
              </div>
            </div>
          </div>
        )}

        {/* ABA: Informações Adicionais */}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="tempoResposta" className="form-label">Tempo de Resposta (dias)</label>
                <input
                  type="number"
                  name="tempoResposta"
                  value={formData.metadata.tempoResposta}
                  onChange={handleMetadataChange}
                  className="form-group-input"
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="avaliacao" className="form-label">Avaliação (1-5)</label>
                <input
                  type="number"
                  name="avaliacao"
                  value={formData.metadata.avaliacao}
                  onChange={handleMetadataChange}
                  className="form-group-input"
                  min="1"
                  max="5"
                  step="0.1"
                />
              </div>

              <div className="form-group">
                <label htmlFor="dataInicioParceria" className="form-label">Início da Parceria</label>
                <input
                  type="date"
                  name="dataInicioParceria"
                  value={formData.metadata.dataInicioParceria}
                  onChange={handleMetadataChange}
                  className="form-group-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="website" className="form-label">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.metadata.website}
                  onChange={handleMetadataChange}
                  className="form-group-input"
                  placeholder="https://..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="linkedin" className="form-label">LinkedIn</label>
                <input
                  type="url"
                  name="linkedin"
                  value={formData.metadata.redesSociais.linkedin}
                  onChange={handleRedesSociaisChange}
                  className="form-group-input"
                  placeholder="https://linkedin.com/..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="instagram" className="form-label">Instagram</label>
                <input
                  type="url"
                  name="instagram"
                  value={formData.metadata.redesSociais.instagram}
                  onChange={handleRedesSociaisChange}
                  className="form-group-input"
                  placeholder="https://instagram.com/..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="facebook" className="form-label">Facebook</label>
                <input
                  type="url"
                  name="facebook"
                  value={formData.metadata.redesSociais.facebook}
                  onChange={handleRedesSociaisChange}
                  className="form-group-input"
                  placeholder="https://facebook.com/..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCloseForm}
            className="action-button action-button-secondary"
          >
            <XCircle size={18} className="icon" />
            Cancelar
          </button>

          <FormStepNavigation
            currentStep={currentStep}
            totalSteps={STEP_LABELS.length}
            onBack={() => setCurrentStep((s) => s - 1)}
            onNext={() => setCurrentStep((s) => s + 1)}
            isSubmitting={isLoading}
            submitLabel={fornecedor ? 'Atualizar Fornecedor' : 'Salvar Fornecedor'}
          />
        </div>
    </form>
  );
};

export default FornecedorForm;